import type React from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { TagBasedAnalysis } from '../../types/reporting.types';
import { formatEuro } from '../../utils/currency';

interface TagAnalysisChartProps {
  tagAnalysis: TagBasedAnalysis[];
}

const TagAnalysisChart: React.FC<TagAnalysisChartProps> = ({ tagAnalysis }) => {
  // Separate income and expense data
  const incomeData = tagAnalysis
    .filter((tag) => tag.type === 'income' || tag.type === 'deposit')
    .map((tag) => ({
      tag: tag.tag_display_name,
      costpoint: tag.costpoint.toUpperCase(),
      budgeted: tag.budgeted_amount,
      actual: tag.actual_amount,
      variance: tag.variance,
      performance: tag.variance >= 0 ? 'under_budget' : 'over_budget',
    }));

  const expenseData = tagAnalysis
    .filter((tag) => tag.type === 'expenses' || tag.type === 'expenses' || tag.type === 'payment')
    .map((tag) => ({
      tag: tag.tag_display_name,
      costpoint: tag.costpoint.toUpperCase(),
      budgeted: tag.budgeted_amount,
      actual: tag.actual_amount,
      variance: tag.variance,
      performance: tag.variance >= 0 ? 'under_budget' : 'over_budget',
    }));

  // Custom tooltip
  interface TagTooltipData {
    costpoint: string;
    variance: number;
  }
  const CustomTooltip = ({
    active,
    payload,
    label,
  }: {
    active?: boolean;
    payload?: { value: number; payload: TagTooltipData }[];
    label?: string;
  }) => {
    if (active && payload?.length && payload[0]) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border border-gray-300 rounded shadow-lg">
          <p className="font-medium text-gray-900">{label}</p>
          <p className="text-xs text-gray-600">{data.costpoint}</p>
          <p className="text-sm text-blue-600">
            <span>Budgeted:</span> {formatEuro(payload[0]?.value ?? 0)}
          </p>
          <p className="text-sm text-green-600">
            <span>Actual:</span> {formatEuro(payload[1]?.value ?? 0)}
          </p>
          <p className="text-sm text-gray-600">
            <span>Variance:</span> {formatEuro(data.variance)}
          </p>
        </div>
      );
    }
    return null;
  };

  if (tagAnalysis.length === 0) {
    return (
      <div className="text-center text-gray-500 py-8">
        <p>No tag data available for analysis</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Income Chart */}
      {incomeData.length > 0 && (
        <div>
          <h4 className="text-md font-medium text-gray-900 mb-4">Income Analysis by Tag</h4>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={incomeData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="tag" angle={-45} textAnchor="end" height={80} interval={0} />
              <YAxis tickFormatter={(value) => `€${(value / 1000).toFixed(0)}K`} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="budgeted" fill="#3B82F6" name="Budgeted" />
              <Bar dataKey="actual" fill="#10B981" name="Actual" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Expenses Chart */}
      {expenseData.length > 0 && (
        <div>
          <h4 className="text-md font-medium text-gray-900 mb-4">Expenses Analysis by Tag</h4>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={expenseData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="tag" angle={-45} textAnchor="end" height={80} interval={0} />
              <YAxis tickFormatter={(value) => `€${(value / 1000).toFixed(0)}K`} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="budgeted" fill="#3B82F6" name="Budgeted" />
              <Bar dataKey="actual" fill="#F97316" name="Actual" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Detailed Numbers Summary */}
      <div className="mt-6">
        <h4 className="text-md font-medium text-gray-900 mb-4">Detailed Tag Analysis</h4>
        <div className="space-y-4">
          {tagAnalysis.map((tag, index) => (
            <div
              key={`${tag.costpoint}-${tag.tag_name}-${index}`}
              className="bg-gray-50 rounded-lg p-4"
            >
              <div className="flex items-center justify-between mb-3">
                <h5 className="font-medium text-gray-900">{tag.tag_display_name}</h5>
                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                  {tag.costpoint.toUpperCase()}
                </span>
              </div>

              <div className="grid grid-cols-4 gap-4 text-sm">
                <div className="text-center">
                  <div className="text-gray-600 mb-1">Type</div>
                  <div
                    className={`font-medium px-2 py-1 rounded text-xs ${
                      tag.type === 'income' || tag.type === 'deposit'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-orange-100 text-orange-800'
                    }`}
                  >
                    {tag.type === 'income' || tag.type === 'deposit' ? 'Income' : 'Expenses'}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-gray-600 mb-1">Budgeted</div>
                  <div className="font-medium text-blue-600">{formatEuro(tag.budgeted_amount)}</div>
                </div>
                <div className="text-center">
                  <div className="text-gray-600 mb-1">Actual</div>
                  <div
                    className={`font-medium ${
                      tag.type === 'income' || tag.type === 'deposit'
                        ? 'text-green-600'
                        : 'text-orange-600'
                    }`}
                  >
                    {formatEuro(tag.actual_amount)}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-gray-600 mb-1">Variance</div>
                  <div
                    className={`font-medium ${
                      tag.variance >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}
                  >
                    {tag.variance >= 0 ? '+' : ''}
                    {formatEuro(Math.abs(tag.variance))}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    ({tag.variance_percentage >= 0 ? '+' : ''}
                    {tag.variance_percentage.toFixed(1)}%)
                  </div>
                </div>
              </div>

              {/* Individual Transaction Lines */}
              {tag.transactions && tag.transactions.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <h6 className="text-sm font-medium text-gray-900 mb-3">
                    Individual Transactions ({tag.transactions.length})
                  </h6>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {tag.transactions.map((transaction, txIndex) => {
                      return (
                        <div
                          key={`${transaction.transaction_id}-${txIndex}`}
                          className="flex items-center justify-between p-2 bg-white rounded border border-gray-100 text-xs"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-900 truncate">
                              {transaction.heading}
                            </div>
                            {transaction.description && (
                              <div className="text-gray-500 truncate mt-1">
                                {transaction.description}
                              </div>
                            )}
                            <div className="text-gray-400 mt-1">
                              {new Date(transaction.date).toLocaleDateString('fi-FI')}
                            </div>
                          </div>
                          <div className="flex-shrink-0 ml-4 text-right">
                            <div className="font-medium text-gray-900">
                              {formatEuro(transaction.allocated_amount)}
                            </div>
                            <div className="text-gray-500 text-xs">
                              allocated to {tag.tag_display_name}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TagAnalysisChart;
